import React from "react";
import { Box, IconButton, Typography, Skeleton, Button, Fade, Slide } from "@mui/material";
import { X, ExternalLink, ShoppingBag, Sparkles, ArrowRight, Search } from "lucide-react";
import { styled } from "@mui/material/styles";


const ResultsCard = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  background: "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)",
  border: "2px solid rgba(139, 92, 246, 0.3)",
  padding: theme.spacing(3),
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    border: "2px solid rgba(139, 92, 246, 0.5)",
    boxShadow: "0 8px 24px rgba(139, 92, 246, 0.3)",
    background: "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #8b5cf6 0%, #a78bfa 50%, #8b5cf6 100%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 3s linear infinite",
  },
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
}));

const IconContainer = styled(Box)({
  width: "64px",
  height: "64px",
  borderRadius: "16px",
  background: "rgba(139, 92, 246, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "16px",
  "& svg": {
    color: "#8b5cf6",
  },
});


const SidebarContainer = styled(Box)<{ open: boolean }>(({ theme, open }) => ({
  position: "fixed",
  right: 0,
  top: 0,
  height: "100vh",
  width: "420px",
  backgroundColor: "#1a1a2e",
  borderLeft: "1px solid rgba(139, 92, 246, 0.2)",
  transform: open ? "translateX(0)" : "translateX(100%)",
  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  zIndex: 1300,
  display: "flex",
  flexDirection: "column",
  boxShadow: open ? "-8px 0 24px rgba(0, 0, 0, 0.4)" : "none",
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderBottom: "1px solid rgba(139, 92, 246, 0.2)",
  background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(26, 26, 46, 0.8) 100%)",
  backdropFilter: "blur(10px)",
}));

const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(3),
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(139, 92, 246, 0.3)",
    borderRadius: "4px",
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.spacing(1.5),
  overflow: "hidden",
  marginBottom: theme.spacing(3),
  background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
  border: "1px solid rgba(139, 92, 246, 0.2)",
  padding: "1px",
  "& img": {
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: theme.spacing(1.5),
  },
}));

const LoadingContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 0",
  gap: "24px",
});

const LoadingAnimation = styled(Box)({
  position: "relative",
  width: "64px",
  height: "64px",
  "& .ring": {
    position: "absolute",
    width: "100%",
    height: "100%",
    border: "3px solid transparent",
    borderTopColor: "#8b5cf6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  "& .ring:nth-of-type(2)": {
    width: "75%",
    height: "75%",
    top: "12.5%",
    left: "12.5%",
    borderTopColor: "#a78bfa",
    animationDelay: "0.2s",
  },
  "& .ring:nth-of-type(3)": {
    width: "50%",
    height: "50%",
    top: "25%",
    left: "25%",
    borderTopColor: "#c4b5fd",
    animationDelay: "0.4s",
  },
  "@keyframes spin": {
    to: { transform: "rotate(360deg)" },
  },
});

const ResultsContainer = styled(Box)(({ theme }) => ({
  "& .results-image": {
    width: "100%",
    borderRadius: theme.spacing(1.5),
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid transparent",
    "&:hover": {
      transform: "scale(1.02)",
      border: "1px solid rgba(139, 92, 246, 0.3)",
      boxShadow: "0 8px 24px rgba(139, 92, 246, 0.2)",
    },
  },
}));

interface ProductSearchSidebarProps {
  open: boolean;
  onClose: () => void;
  selectedImage?: string | null;
  isLoading: boolean;
  searchResults?: string | null;
  googleLensUrl?: string | null;
}

export const ProductSearchSidebar = ({
  open,
  onClose,
  selectedImage,
  isLoading,
  searchResults,
  googleLensUrl,
}: ProductSearchSidebarProps) => {
  return (
    <SidebarContainer open={open}>
      <Header>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <ShoppingBag style={{ color: "#8b5cf6" }} />
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 500 }}>
              Product Search
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#8b5cf6" }}>
            <X />
          </IconButton>
        </Box>
      </Header>

      <Content>
        {selectedImage && (
          <Fade in timeout={500}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: "#8b5cf6", mb: 1.5, fontWeight: 500 }}>
                Your Selection
              </Typography>
              <ImageContainer>
                <img src={selectedImage} alt="Selected product" />
              </ImageContainer>
            </Box>
          </Fade>
        )}

        {isLoading && (
          <LoadingContainer>
            <LoadingAnimation>
              <div className="ring" />
              <div className="ring" />
              <div className="ring" />
            </LoadingAnimation>
            <Box textAlign="center">
              <Typography variant="body1" sx={{ color: "#fff", mb: 0.5 }}>
                Searching for similar products
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                This usually takes a few seconds...
              </Typography>
            </Box>
          </LoadingContainer>
        )}

        {!isLoading && googleLensUrl && (
          <Fade in timeout={500}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Sparkles size={20} style={{ color: "#8b5cf6" }} />
                <Typography variant="subtitle2" sx={{ color: "#8b5cf6", fontWeight: 500 }}>
                  Search Results Ready!
                </Typography>
              </Box>
              
              <ResultsCard
                onClick={() => window.open(googleLensUrl, '_blank')}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') window.open(googleLensUrl, '_blank');
                }}
              >
                <IconContainer>
                  <Search size={32} />
                </IconContainer>
                
                <Typography variant="h6" sx={{ color: "#fff", mb: 1, fontWeight: 600 }}>
                  View Your Results
                </Typography>
                
                <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 2 }}>
                  We found similar products on Google Shopping. Click to explore matches, prices, and where to buy.
                </Typography>
                
                <Box display="flex" alignItems="center" gap={1} sx={{ color: "#8b5cf6" }}>
                  <Typography variant="body2" fontWeight={500}>
                    Open in Google Lens
                  </Typography>
                  <ArrowRight size={18} />
                </Box>
              </ResultsCard>
              
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ExternalLink size={18} />}
                onClick={() => window.open(googleLensUrl, '_blank')}
                sx={{
                  mt: 2,
                  borderColor: "rgba(139, 92, 246, 0.5)",
                  color: "#8b5cf6",
                  "&:hover": {
                    borderColor: "#8b5cf6",
                    backgroundColor: "rgba(139, 92, 246, 0.05)",
                  },
                }}
              >
                Open in New Tab
              </Button>
              
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "rgba(255, 255, 255, 0.5)",
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                Powered by Google Lens
              </Typography>
            </Box>
          </Fade>
        )}
      </Content>
    </SidebarContainer>
  );
};